import { StaffRole } from '@urbanx/agx.auth.client';
import { UrbanXTeamByAgenciesResponse } from '@urbanx/agx.agencies.client';
import { AgxButton, AgxModal, AgxRow, AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';

interface Props {
  roleConflicts: UrbanXTeamByAgenciesResponse[];
  selectedRoles?: string[];
  setShowModal: (showModal: boolean) => void;
  updateRoleAssignments: () => void;
  onCancel?: () => void;
}

const formatRoleName = (role: string | undefined) => {
  switch (role) {
    case StaffRole.BusinessManager:
      return 'Business Manager';
    case StaffRole.AdminManager:
      return 'Admin Manager';
    case StaffRole.MarketingExpert:
      return 'Marketing Expert';
    case StaffRole.SuperAdmin:
      return 'Super Admin';
    case StaffRole.Executive:
      return 'Executive';
    case StaffRole.ListingsContributor:
      return 'Admin Listings';
    case StaffRole.LaunchContributor:
      return 'Admin Launch';
    case StaffRole.ContractsContributor:
      return 'Admin Contract';
    case StaffRole.TransactionsContributor:
      return 'Admin Transactions';
    case StaffRole.FinanceContributor:
      return 'Admin Finance';
    default:
      return undefined;
  }
};

export const RoleAssignmentModal = (props: Props) => {
  const { roleConflicts, selectedRoles, setShowModal, updateRoleAssignments, onCancel } = props;
  const rolesText = [
    ...new Set(roleConflicts.map((x) => x.staffRole).filter((role): role is string => !!role)),
  ]
    .concat(selectedRoles ?? [])
    .map(formatRoleName)
    .filter((role) => role !== undefined)
    .join(', ');

  const actionButtons = (
    <AgxRow
      gap={'spacing-md'}
      justifyContent="center"
      fill
      padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
    >
      <AgxButton
        id={'btn-replace-assignment'}
        key={'perform-role-assignment'}
        variant={'primary'}
        size={'large'}
        text={'Replace'}
        onClick={() => updateRoleAssignments()}
        stretch
      />
      <AgxButton
        id={'btn-cancel-assignment'}
        key={'cancel-role-assignment'}
        variant={'hollow'}
        size={'large'}
        text={'Cancel'}
        onClick={() => {
          setShowModal(false);
          onCancel?.();
        }}
        stretch
      />
    </AgxRow>
  );
  const warningContent = (
    <AgxSpacer
      direction="vertical"
      fill
      gap="sm"
      padding={{ top: 'sm', bottom: 'xl', left: 'xl', right: 'xl' }}
    >
      <AgxTypography
        id="txt-role-assignment-summary"
        type="body_ui"
        size="small"
        color="var(--text-secondary)"
      >
        This will replace the current assignment in the following agencies:
      </AgxTypography>
      {roleConflicts.map((roleConflict) =>
        (roleConflict.staffRoleAssignments ?? []).map((x) => (
          <AgxTypography
            key={`${x.agencyId}-${x.staffMemberId}-${roleConflict.staffRole ?? 'role'}`}
            id={`txt-role-assignment-${x.agencyId}-${roleConflict.staffRole ?? 'role'}`}
            type="body_ui"
            size="small"
            color="var(--text-secondary)"
          >
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
              {x.agencyName ?? 'Agency'}
            </span>
            {' - '}
            {`${
              (formatRoleName(roleConflict.staffRole) ?? rolesText) || 'Selected role'
            } assignment is currently held by `}
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
              {`${x.firstName ?? ''} ${x.lastName ?? ''}`.trim() || 'Unknown'}
            </span>
          </AgxTypography>
        ))
      )}
    </AgxSpacer>
  );

  return (
    <AgxModal
      title={'Replace current assignment?'}
      type={'single'}
      variant={'withDivider'}
      actionButtonContainerStyle={'withoutDivider'}
      content={warningContent}
      actionButtons={actionButtons}
      setIsDialogOpen={(isOpen) => {
        setShowModal(isOpen);
        if (!isOpen) onCancel?.();
      }}
    />
  );
};

export default RoleAssignmentModal;
